/** @module Load images that need an Authorization header and hand back object URLs. */
import { useEffect, useState } from "react";

import { accessToken, refresh } from "@/lib/session";

export type ImageState = "loading" | "ready" | "missing";

async function fetchImage(path: string): Promise<Blob | null> {
  const get = (token: string | null) =>
    fetch(path, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
  let response = await get(accessToken());
  // The access token lives 15 minutes; a picture asked for after that must not stay blank.
  if (response.status === 401) {
    const token = await refresh();
    if (!token) return null;
    response = await get(token);
  }
  return response.ok ? await response.blob() : null;
}

/** An <img src> cannot carry a bearer token, so the bytes are fetched and wrapped. */
export function useImage(path: string | null): { url: string | null; state: ImageState } {
  const [result, setResult] = useState<{ path: string | null; url: string | null; ok: boolean }>({
    path: null,
    url: null,
    ok: false,
  });

  useEffect(() => {
    if (!path) return;
    let revoked = false;
    let objectUrl: string | null = null;
    void fetchImage(path)
      .then((blob) => {
        if (revoked) return;
        objectUrl = blob ? URL.createObjectURL(blob) : null;
        setResult({ path, url: objectUrl, ok: blob !== null });
      })
      .catch(() => {
        if (!revoked) setResult({ path, url: null, ok: false });
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  // A result for another path is stale: show "loading", not the previous card.
  if (!path || result.path !== path) return { url: null, state: "loading" };
  return { url: result.url, state: result.ok ? "ready" : "missing" };
}

/** The object URL alone, for places that only show a picture or a grey box. */
export function useAuthedImage(path: string | null): string | null {
  return useImage(path).url;
}
